import { coordinateInPlaceViewport } from '@components/Explorer/MapGeometry';
import { PlaceAutocompleteResult, PlaceViewport } from '@db/Places';
import { dateRangesOverlap, filterNonPastPlans, filterPastPlans } from '@helpers/Dates';
import { placesOverlap } from '@helpers/Geography';
import { DateRange, Group, GroupSearchResult, Place, Plan, User, UserSearchResult } from '@types';

export const PLACE_SEARCH_TYPES = ['locality', 'country'];

export type ExplorerDisplayMode = 'residences' | 'upcoming' | 'visited';

export type PlaceSearchResult = PlaceAutocompleteResult;

export type SelectedSearchFilter =
  | { type: 'user'; user: UserSearchResult }
  | { type: 'group'; group: GroupSearchResult }
  | { type: 'place'; place: Place; viewport?: PlaceViewport };

export type SearchResultFilter =
  | { type: 'user'; user: UserSearchResult }
  | { type: 'group'; group: GroupSearchResult }
  | { type: 'place'; place: PlaceSearchResult };

export type SearchResult = {
  type: SearchResultFilter['type'];
  id: string;
  icon: 'account' | 'account-multiple' | 'map-marker';
  label: string;
  detail?: string;
  filter: SearchResultFilter;
};

export type SearchTargets = {
  users: boolean;
  groups: boolean;
  places: boolean;
};

type PlanLocation = Plan['location'];

export function addSearchFilter(filters: SelectedSearchFilter[], filter: SelectedSearchFilter) {
  return filters.some((currentFilter) => getFilterKey(currentFilter) === getFilterKey(filter))
    ? filters
    : [filter, ...filters];
}

export function getVisibleUserResults(
  userResults: UserSearchResult[],
  users: User[],
  searchUserIds?: string[]
) {
  const visibleUserIds = new Set(searchUserIds ?? users.map((user) => user.userId));
  return userResults.filter((result) => visibleUserIds.has(result.userId));
}

export function getVisibleGroupResults(
  groupResults: GroupSearchResult[],
  groups: Group[],
  searchGroupIds?: string[]
) {
  const visibleGroupIds = new Set(searchGroupIds ?? groups.map((group) => group.groupId));
  return groupResults.filter((result) => visibleGroupIds.has(result.groupId));
}

export function getSearchResults(
  visibleUserResults: UserSearchResult[],
  visibleGroupResults: GroupSearchResult[],
  placeResults: PlaceSearchResult[],
  searchTargets: SearchTargets,
  selectedFilters: SelectedSearchFilter[]
): SearchResult[] {
  const selectedFilterKeys = new Set(selectedFilters.map(getFilterKey));
  const users = searchTargets.users
    ? visibleUserResults.slice(0, 6).map(
        (user): SearchResult => ({
          type: 'user',
          id: user.userId,
          icon: 'account',
          label: user.displayName,
          detail: user.hometown.displayName,
          filter: { type: 'user', user },
        })
      )
    : [];
  const groups = searchTargets.groups
    ? visibleGroupResults.slice(0, 6).map(
        (group): SearchResult => ({
          type: 'group',
          id: group.groupId,
          icon: 'account-multiple',
          label: group.displayName,
          filter: { type: 'group', group },
        })
      )
    : [];
  const places = searchTargets.places
    ? placeResults.slice(0, 6).map(
        (place): SearchResult => ({
          type: 'place',
          id: place.placeId,
          icon: 'map-marker',
          label: place.displayName,
          filter: { type: 'place', place },
        })
      )
    : [];

  return [...users, ...groups, ...places].filter(
    (result) => !selectedFilterKeys.has(getFilterKey(result.filter))
  );
}

export function getSearchResultKey(result: SearchResult) {
  return `${result.type}:${result.id}`;
}

export function filterPlans(
  plans: Plan[],
  selectedFilters: SelectedSearchFilter[],
  dateRange?: DateRange,
  displayMode?: ExplorerDisplayMode
) {
  let filteredPlans = dateRange
    ? plans.filter((plan) =>
        dateRangesOverlap({ startDate: plan.startDate, endDate: plan.endDate }, dateRange)
      )
    : plans;

  if (selectedFilters.length > 0) {
    filteredPlans = filteredPlans.filter((plan) =>
      selectedFilters.some((filter) => planMatchesFilter(plan, filter))
    );
  }

  if (displayMode === 'residences') return [];
  if (displayMode === 'upcoming') return filterNonPastPlans(filteredPlans);
  if (displayMode === 'visited') return filterPastPlans(filteredPlans);

  return filteredPlans;
}

export function filterUsers(
  users: User[],
  selectedFilters: SelectedSearchFilter[],
  displayMode?: ExplorerDisplayMode
) {
  if (displayMode === 'upcoming' || displayMode === 'visited') return [];

  const visibleUserFilters = selectedFilters.filter(
    (filter) => filter.type === 'user' || filter.type === 'group' || filter.type === 'place'
  );
  if (visibleUserFilters.length === 0) return users;

  return users.filter((user) =>
    visibleUserFilters.some((filter) => {
      if (filter.type === 'user') return user.userId === filter.user.userId;
      if (filter.type === 'group') return user.groupIds?.includes(filter.group.groupId);
      if (filter.type === 'place') return locationMatchesPlace(user.hometown, filter);
      return false;
    })
  );
}

export function getFilterLabel(filter: SelectedSearchFilter | SearchResultFilter) {
  switch (filter.type) {
    case 'user':
      return filter.user.displayName;
    case 'group':
      return filter.group.displayName;
    case 'place':
      return filter.place.displayName;
  }
}

export function getFilterKey(filter: SelectedSearchFilter | SearchResultFilter) {
  switch (filter.type) {
    case 'user':
      return `${filter.type}:${filter.user.userId}`;
    case 'group':
      return `${filter.type}:${filter.group.groupId}`;
    case 'place':
      return `${filter.type}:${filter.place.placeId}`;
  }
}

function planMatchesFilter(plan: Plan, filter: SelectedSearchFilter) {
  switch (filter.type) {
    case 'user':
      return plan.userId === filter.user.userId || plan.userIds?.includes(filter.user.userId);
    case 'group':
      return plan.groupIds?.includes(filter.group.groupId);
    case 'place':
      return locationMatchesPlace(plan.location, filter);
  }
}

function locationMatchesPlace(
  location: PlanLocation | undefined,
  filter: Extract<SelectedSearchFilter, { type: 'place' }>
) {
  if (!location) return false;
  if (location.placeId === filter.place.placeId) return true;
  const viewportMatch = coordinateInPlaceViewport(location, filter.viewport);
  if (typeof viewportMatch === 'boolean') return viewportMatch;
  return placesOverlap(location, filter.place);
}
